import { NSARouterMakerResult } from "namirasoft-account-react";
import { NSBoxDateRange, NSButtonBlue, NSChartBar, NSChartDoughnut, NSColumn, NSLabel, NSLoading, NSPanel, NSRow, NSSection, NSSectionTitle, } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { JobArrangerLayout } from "../JobArrangerLayout";
import { JobArrangerMenus } from "../JobArrangerMenus";
import { JobArrangerProps } from "../JobArrangerProps";
import { DashboardRow } from "../rows/DashboardRow";
import Style from "./DashboardPage.module.css";
import { TimeOperation } from "namirasoft-core";
import { JobStatusObject } from "../JobStatusObject";

interface DashboardPageProps extends NSARouterMakerResult, JobArrangerProps { }

interface DashboardPageState
{
	row: DashboardRow | null;
	loading: boolean;
}

const PLATFORM_COLORS: { [name: string]: string } = {
	LinkedIn: "#0A66C2",
	Indeed: "#fa6400",
	Glassdoor: "#0CAA41",
	Monster: "#6E46AE",
	JobBank: "#D52B1E",
	None: "#888888",
};

function getStatusColor(name: string, fallback: string): string
{
	return JobStatusObject.get(name)?.color ?? fallback;
}

const FALLBACK_COLORS: string[] = [
	"#FF5733", "#42A5F5", "#FFC107", "#8BC34A", "#FF5722",
	"#673AB7", "#E91E63", "#4CAF50", "#FF9800", "#9C27B0",
];

export default class DashboardPage extends Component<DashboardPageProps, DashboardPageState>
{
	private NSBoxDateRange_DateRange = createRef<NSBoxDateRange>();
	private defaultFromDate: Date;
	private defaultToDate: Date;

	constructor(props: DashboardPageProps)
	{
		super(props);

		const now = new Date();
		now.setHours(23, 59, 59, 999);
		this.defaultToDate = now;
		const weekAgo = TimeOperation.weeksAgo(1, now);
		weekAgo.setHours(0, 0, 0, 0);
		this.defaultFromDate = weekAgo;
		this.state = { row: null, loading: true };
	}

	override componentDidMount(): void
	{
		this.load(this.defaultFromDate, this.defaultToDate);
	}

	private load(from: Date, to: Date)
	{
		this.setState({ loading: true });
		this.props.servers.job_arranger.user.DashboardFromToDate(from, to).then(row =>
		{
			this.setState({ row, loading: false });
		}).catch(e =>
		{
			console.error("DashboardFromToDate failed:", e);
			this.setState({ loading: false }); 
		});
	}

	private onApply = (onFinished: () => void) =>
	{
		const dateRange = this.NSBoxDateRange_DateRange.current?.getValue(false);
		const fromStr = dateRange?.from ?? this.defaultFromDate;
		const toStr = dateRange?.to ?? this.defaultToDate;
		this.load(fromStr, toStr);
		onFinished();
	};

	private renderNumberCards(title: string, items: { name: string, count: number }[], colorOf: (name: string, index: number) => string)
	{
		if (!items || items.length === 0) return null;
		const total = items.reduce((sum, it) => sum + Number(it.count || 0), 0);
		return (
			<NSSectionTitle title={`${title} (Total: ${total})`}>
				<NSRow style={{ flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
					{items.map((it, i) => (
						<NSPanel
							key={it.name}
							style={{
								borderTop: `4px solid ${colorOf(it.name, i)}`,
								minWidth: "140px",
								textAlign: "center",
							}}
						>
							<NSLabel title={String(it.count)}
								description={it.name}
							>

							</NSLabel>
						</NSPanel>
					))}
				</NSRow>
			</NSSectionTitle>
		);
	}

	private renderAppliedChart(appliedByDay: { day: string, platform: string, count: number }[])
	{
		if (!appliedByDay || appliedByDay.length === 0) return null;

		const daySet = new Set<string>();
		const platformSet = new Set<string>();
		for (const it of appliedByDay)
		{
			daySet.add(it.day);
			platformSet.add(it.platform || "None");
		}
		const days = Array.from(daySet).sort();
		const platforms = Array.from(platformSet).sort();

		const lookup: { [key: string]: number } = {};
		for (const it of appliedByDay)
			lookup[`${it.day}|${it.platform || "None"}`] = Number(it.count || 0);

		const dataset = platforms.map((p, i) => ({
			label: p,
			color: PLATFORM_COLORS[p] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
			data: days.map(d => lookup[`${d}|${p}`] ?? 0),
		}));

		const max = days.reduce((m, d) =>
			Math.max(m, platforms.reduce((s, p) => s + (lookup[`${d}|${p}`] ?? 0), 0)), 0);

		return (
			<NSSectionTitle title="Applied per day">
				<NSPanel style={{ width: "100%" }}>
					<NSChartBar
						labels={days}
						dataset={dataset}
						mode={"stacked"}
						range={{ min: 0, max: Math.max(1, max) }}
						yGrid={true}
						height={360}
					/>
				</NSPanel>
			</NSSectionTitle>
		);
	}

	override render()
	{
		const { row, loading } = this.state;

		let dataset_status: { labels: string[], values: number[], colors: string[] } | null = null;
		let dataset_platform: { labels: string[], values: number[], colors: string[] } | null = null;
		if (row)
		{
			if (row.byStatus.length > 0)
				dataset_status = {
					labels: row.byStatus.map(x => x.name),
					values: row.byStatus.map(x => x.count),
					colors: row.byStatus.map((x, i) => getStatusColor(x.name, FALLBACK_COLORS[i % FALLBACK_COLORS.length]))
				};
			if (row.byPlatform.length > 0)
				dataset_platform = {
					labels: row.byPlatform.map(x => x.name),
					values: row.byPlatform.map(x => x.count),
					colors: row.byPlatform.map((x, i) => PLATFORM_COLORS[x.name] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length])
				};
		}

		const hasAny = row && (row.byStatus.length > 0 || row.byPlatform.length > 0 || (row.appliedByDay?.length ?? 0) > 0);

		return (
			<JobArrangerLayout {...this.props}
				action={{
					title: "Dashboard",
					description: "Comprehensive reports, status-based and platform-based, to help you stay informed about your job search progress and activities.",
					menus: { "Store": [JobArrangerMenus.download()] }
				}}
			>
				<NSColumn>
					<NSSectionTitle title="Filters">
						<NSPanel style={{ width: "100%" }}>
							<NSRow>
								<NSBoxDateRange
									ref={this.NSBoxDateRange_DateRange}
									title="Date Range"
									required={true}
									preset={true}
									hideHeader={true}
									defaultValue={{
										from: this.defaultFromDate,
										to: this.defaultToDate
									}}
								/>
								<NSButtonBlue
									title="Apply"
									onClick={{ action: this.onApply, showLoading: true }}
								/>
							</NSRow>
						</NSPanel>
					</NSSectionTitle>

					{loading && <NSLoading />}

					{!loading && row && this.renderAppliedChart(row.appliedByDay ?? [])}

					{!loading && <NSSection>
						<NSRow classList={[Style.parent_chart]} style={{ width: "100%", justifyContent: "center", flexWrap: "wrap", gap: "24px" }}>
							{
								dataset_status &&
								<NSColumn style={{ maxWidth: "360px" }}>
									<NSChartDoughnut
										labels={dataset_status.labels}
										datas={dataset_status.values}
										backgroundColors={dataset_status.colors}
										borderWidth={1}
										title="Status"
									/>
								</NSColumn>
							}
							{
								dataset_platform &&
								<NSColumn style={{ maxWidth: "360px" }}>
									<NSChartDoughnut
										labels={dataset_platform.labels}
										datas={dataset_platform.values}
										backgroundColors={dataset_platform.colors}
										borderWidth={1}
										title="Platform"
									/>
								</NSColumn>
							}
						</NSRow>
					</NSSection>}

					{!loading && row && this.renderNumberCards("By Platform", row.byPlatform,
						(name, i) => PLATFORM_COLORS[name] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length])}

					{!loading && row && this.renderNumberCards("By Status", row.byStatus,
						(name, i) => getStatusColor(name, FALLBACK_COLORS[i % FALLBACK_COLORS.length]))}

					{
						!loading && !hasAny &&
						<NSSection>
							<NSRow>
								<NSLabel title="There is no data yet to show the dashboard.">
									<></>
								</NSLabel>
							</NSRow>
						</NSSection>
					}
				</NSColumn>
			</JobArrangerLayout>
		);
	}
}